export type SupportedCurrency = 'DKK' | 'EUR' | 'USD' | 'GBP' | 'CHF'

export const CURRENCY_OPTIONS: { value: SupportedCurrency; label: string }[] = [
  { value: 'DKK', label: 'DKK (Danish Krone)' },
  { value: 'EUR', label: 'EUR (Euro)' },
  { value: 'USD', label: 'USD (US Dollar)' },
  { value: 'GBP', label: 'GBP (British Pound)' },
  { value: 'CHF', label: 'CHF (Swiss Franc)' },
]

/**
 * Exchange rates from DKK to other currencies.
 * Each value represents how many units of the target currency equal 1 DKK.
 * DKK is the base currency (rate = 1).
 *
 * Last updated: 2026-02-26
 */
export const EXCHANGE_RATES_FROM_DKK: Record<SupportedCurrency, number> = {
  DKK: 1,
  EUR: 0.1341,
  USD: 0.1444,
  GBP: 0.1148,
  CHF: 0.1275,
}

/**
 * Convert a value from DKK to the target currency using static exchange rates.
 */
export function convertFromDKK(value: number, currency: SupportedCurrency): number {
  if (currency === 'DKK') return value
  return value * EXCHANGE_RATES_FROM_DKK[currency]
}

const CURRENCY_LOCALES: Record<SupportedCurrency, string> = {
  DKK: 'da-DK',
  EUR: 'de-DE',
  USD: 'en-US',
  GBP: 'en-GB',
  CHF: 'de-CH',
}

const formatterCache = new Map<SupportedCurrency, Intl.NumberFormat>()

function getFormatter(currency: SupportedCurrency): Intl.NumberFormat {
  let formatter = formatterCache.get(currency)
  if (!formatter) {
    formatter = new Intl.NumberFormat(CURRENCY_LOCALES[currency], {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    })
    formatterCache.set(currency, formatter)
  }
  return formatter
}

/**
 * Format a value in the specified currency.
 * Defaults to DKK for backwards compatibility.
 */
export function formatCurrency(value: number | null, currency: SupportedCurrency = 'DKK'): string {
  if (value === null || value === 0) return '-'
  return getFormatter(currency).format(value)
}

/**
 * @deprecated Use formatCurrency() instead. Kept for backwards compatibility.
 */
export function formatDKK(value: number | null): string {
  return formatCurrency(value, 'DKK')
}
